/**
 * SharedVerifyPage.jsx
 * Unified photo verification page used by all assessment types.
 * Handles link validation, webcam capture, upload fallback, and unified photo submit API.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, CheckCircle2, Upload } from 'lucide-react';
import { apiClient } from '../../api/client';

const VERIFY_STORAGE_KEYS = {
  aptitude: 'aptitude_verify_photo',
  cognitive: 'cognitive_verify_photo',
  skill: 'skill_verify_photo',
  mega: 'mega_verify_photo',
  extended: 'extended_verify_photo',
  edutech: 'edutech_verify_photo',
  salesforce: 'salesforce_verify_photo',
  customercare: 'customercare_verify_photo',
  callingsales: 'callingsales_verify_photo',
  feetonstreet: 'feetonstreet_verify_photo',
  bigfive_productivity: 'bigfive_productivity_verify_photo'
};

function getPhotoStorageKey(assessmentKey) {
  return VERIFY_STORAGE_KEYS[assessmentKey] || `${assessmentKey}_verify_photo`;
}

export default function SharedVerifyPage({ assessmentKey, assessmentLabel, onVerified }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const countdownWaitRef = useRef(null); // FIX: track video-ready polling timer to avoid premature capture.
  const countdownSafetyTimeoutRef = useRef(null); // FIX: track readiness timeout for camera load failures.
  const metadataTimeoutRef = useRef(null); // FIX: holds safety timeout id for onloadedmetadata fallback.
  const capturedPhotoRef = useRef(''); // FIX: mirror capturedPhoto in ref to avoid stale timeout closure checks.
  const isCameraReadyRef = useRef(false); // FIX: tracks successful stream attachment/play state for metadata timeout fallback.
  const streamRef = useRef(null); // FIX: keep MediaStream in ref to avoid re-render loops/flicker.
  const isMountedRef = useRef(false); // FIX: guard mount state to prevent duplicate async camera attach.
  const isStartingCameraRef = useRef(false); // FIX: prevent concurrent getUserMedia calls.

  const [capturedPhoto, setCapturedPhoto] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [photoSource, setPhotoSource] = useState(null);
  const [cameraPermission, setCameraPermission] = useState('pending');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(null);
  const [validationLoading, setValidationLoading] = useState(true);
  const [linkValid, setLinkValid] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [, setAssignmentMeta] = useState({ assessment_key: null, assessment_uid: null });

  useEffect(() => {
    capturedPhotoRef.current = capturedPhoto; // FIX: keep captured photo availability current for async fallback guard.
  }, [capturedPhoto]);

  const cleanupCountdown = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (countdownWaitRef.current) {
      clearInterval(countdownWaitRef.current); // FIX: clear video-ready poller when countdown is reset.
      countdownWaitRef.current = null;
    }
    if (countdownSafetyTimeoutRef.current) {
      clearTimeout(countdownSafetyTimeoutRef.current); // FIX: clear stale safety timeout on reset.
      countdownSafetyTimeoutRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop()); // FIX: stop active stream from ref for stable cleanup.
    streamRef.current = null; // FIX: clear ref so next capture can reinitialize once.
    isStartingCameraRef.current = false; // FIX: reset in-flight camera lock when stream is stopped.
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const captureFromVideo = useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) {
      setError('Camera not ready. Please wait.'); // FIX: explicit readiness error when refs are unavailable.
      return;
    }

    if (video.readyState < 2) {
      setError('Camera is still loading. Please wait a moment.'); // FIX: accept HAVE_CURRENT_DATA for webcam instead of requiring HAVE_ENOUGH_DATA.
      return;
    }

    if (video.videoWidth === 0 || video.videoHeight === 0) {
      setError('No video signal detected. Please allow camera access.'); // FIX: block zero-dimension black captures.
      return;
    }

    canvas.width = video.videoWidth; // FIX: use real stream dimensions for reliable capture.
    canvas.height = video.videoHeight; // FIX: use real stream dimensions for reliable capture.
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('Unable to capture photo. Please try again.'); // FIX: surface canvas context failure.
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const image = canvas.toDataURL('image/jpeg', 0.85);

    if (image.length < 5000) {
      setError('Photo appears blank. Ensure your camera is unobstructed.'); // FIX: reject likely blank/black captures.
      return;
    }

    // ADDED: persist captured verify photo for existing assessment start handlers.
    sessionStorage.setItem(getPhotoStorageKey(assessmentKey), image);
    setCapturedPhoto(image);
    setPhotoSource('webcam');
    setUploadedFile(null);
    setError(null); // FIX: clear transient camera-loading errors after successful capture.
    setCountdown(null);
    cleanupCountdown();
    stopStream();
  }, [assessmentKey, cleanupCountdown, stopStream]);

  const beginCountdown = useCallback(() => {
    setCountdown(3);
    countdownTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          captureFromVideo();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, [captureFromVideo, cleanupCountdown]);

  const startCountdown = useCallback(() => {
    cleanupCountdown();
    setCountdown(null);
    setError('');
    countdownWaitRef.current = setInterval(() => {
      const video = videoRef.current;
      if (video && video.readyState >= 2 && video.videoWidth > 0) { // FIX: readyState 2 is sufficient for webcam current frame data.
        cleanupCountdown();
        beginCountdown(); // FIX: only start countdown once the video is actually streaming frames.
      }
    }, 200);

    countdownSafetyTimeoutRef.current = setTimeout(() => {
      cleanupCountdown();
      if (!capturedPhotoRef.current) {
        setError('Camera took too long to load. Please try the upload option below.'); // FIX: use ref to avoid stale closure and provide non-blocking fallback guidance.
      }
    }, 10000);
  }, [beginCountdown, cleanupCountdown]);

  const startCamera = useCallback(async () => {
    if (isStartingCameraRef.current) return; // FIX: block parallel getUserMedia requests.
    if (streamRef.current) return; // FIX: avoid restarting camera when stream already exists.
    isStartingCameraRef.current = true; // FIX: lock camera start until resolved.
    try {
      const media = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 }, audio: false }); // FIX: request stable preview dimensions for predictable video attach.
      if (!isMountedRef.current) { // FIX: component unmounted before stream resolved.
        media.getTracks().forEach((track) => track.stop()); // FIX: release orphan stream immediately.
        return;
      }
      streamRef.current = media; // FIX: persist active media stream for lifecycle cleanup and capture flow.
      isCameraReadyRef.current = false; // FIX: reset camera-ready state before starting attach/play sequence.
      setCameraPermission('pending-attach'); // FIX: represent intermediate state while waiting to attach and play stream.
      const attachWhenReady = () => { // FIX: attach srcObject only after video element is guaranteed in DOM.
        if (!isMountedRef.current) return; // FIX: stop RAF attach loop when component is unmounted.
        if (!videoRef.current) { // FIX: video ref may not exist yet due to render timing; retry next frame.
          requestAnimationFrame(attachWhenReady); // FIX: retry stream attachment on next animation frame.
          return;
        }
        videoRef.current.srcObject = media; // FIX: attach stream once video DOM node is available.
        videoRef.current.muted = true; // FIX: ensure muted playback for browser autoplay compatibility.
        videoRef.current.onloadedmetadata = () => { // FIX: start playback only after metadata confirms dimensions.
          if (!isMountedRef.current || !videoRef.current) return; // FIX: guard against stale refs during async callback.
          videoRef.current.play()
            .then(() => {
              isCameraReadyRef.current = true; // FIX: mark camera as ready after successful playback start.
              setCameraPermission('granted'); // FIX: switch UI state to active camera once stream is actually playing.
              startCountdown(); // FIX: trigger existing countdown flow only after playback starts.
            })
            .catch((playErr) => {
              console.warn('[Camera] play() failed:', playErr); // FIX: log play failures for browser-policy debugging.
              isCameraReadyRef.current = true; // FIX: prevent timeout fallback loop when play promise rejects but frames may still arrive.
              setCameraPermission('granted'); // FIX: keep UI usable even when play promise behavior differs by browser.
              startCountdown(); // FIX: continue capture flow even if play promise is rejected.
            });
        };
        clearTimeout(metadataTimeoutRef.current); // FIX: clear any previous metadata timeout before scheduling a new one.
        metadataTimeoutRef.current = setTimeout(() => { // FIX: fallback if onloadedmetadata never fires.
          if (!isCameraReadyRef.current && isMountedRef.current) { // FIX: only run fallback while camera is not ready and component is still mounted.
            console.warn('[Camera] metadata timeout — trying play() directly'); // FIX: emit timeout diagnostics for attach failures.
            videoRef.current?.play()
              .then(() => {
                isCameraReadyRef.current = true; // FIX: mark camera ready when fallback play succeeds.
                setCameraPermission('granted'); // FIX: transition UI state after fallback playback.
                startCountdown(); // FIX: continue normal countdown flow from fallback path.
              })
              .catch(() => setCameraPermission('denied')); // FIX: fall back to upload UI when both metadata and play fallback fail.
          }
        }, 6000);
      };
      requestAnimationFrame(attachWhenReady); // FIX: defer initial stream attachment until after React render commits.
    } catch (err) {
      setCameraPermission('denied'); // FIX: treat all camera acquisition failures as denied for fallback UI path.
    } finally {
      isStartingCameraRef.current = false; // FIX: always release camera start lock.
    }
  }, [startCountdown]);

  useEffect(() => {
    if (isMountedRef.current) return undefined; // FIX: prevent duplicate init path in same mount cycle.
    isMountedRef.current = true; // FIX: mark mounted before async validation/camera init.

    const validateLink = async () => {
      const assessmentRef = sessionStorage.getItem('assessment_ref');
      const authToken = sessionStorage.getItem('authToken');

      if (!assessmentRef || !authToken) {
        if (isMountedRef.current) {
          setLinkValid(false);
          setLinkError('This assessment link is no longer valid. Please contact your administrator.');
          setValidationLoading(false);
        }
        return;
      }

      try {
        const response = await apiClient.get('/assessment/validate-ref', {
          params: { ref: assessmentRef },
          headers: { Authorization: `Bearer ${authToken}` }
        });

        if (response.data?.valid && response.data?.assessment_key === assessmentKey) {
          if (!isMountedRef.current) return;
          setAssignmentMeta({
            assessment_key: response.data.assessment_key || null,
            assessment_uid: response.data.assessment_uid || null
          });
          setLinkValid(true);
          setLinkError('');
          await startCamera();
        } else if (isMountedRef.current) {
          setLinkValid(false);
          setLinkError('This assessment link is no longer valid. Please contact your administrator.');
        }
      } catch (_err) {
        if (isMountedRef.current) {
          setLinkValid(false);
          setLinkError('This assessment link is no longer valid. Please contact your administrator.');
        }
      } finally {
        if (isMountedRef.current) setValidationLoading(false);
      }
    };

    validateLink();

    return () => {
      isMountedRef.current = false; // FIX: mark unmounted so pending async work does not mutate state.
      clearTimeout(metadataTimeoutRef.current); // FIX: prevent safety timeout firing after unmount.
      metadataTimeoutRef.current = null; // FIX: clear timeout ref during unmount cleanup.
      isCameraReadyRef.current = false; // FIX: reset camera-ready flag during unmount cleanup lifecycle.
      if (videoRef.current) {
        videoRef.current.onloadedmetadata = null; // FIX: remove metadata event handler on unmount.
      }
      cleanupCountdown();
      stopStream();
    };
  }, []); // FIX: empty deps so camera/validation init runs once per mount only.

  const onUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be 5MB or less.');
      return;
    }

    setError('');
    const reader = new FileReader();
    reader.onload = () => {
      const img = reader.result;
      if (typeof img === 'string') {
        // ADDED: persist uploaded verify photo for existing assessment start handlers.
        sessionStorage.setItem(getPhotoStorageKey(assessmentKey), img);
        cleanupCountdown(); // FIX: stop webcam countdown when user switches to manual upload.
        setCountdown(null); // FIX: hide stale countdown overlay during upload flow.
        setCapturedPhoto(img);
        setUploadedFile(file);
        setPhotoSource('upload');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRetake = async () => {
    setCapturedPhoto('');
    setUploadedFile(null);
    setPhotoSource(null);
    setError('');
    setCountdown(null); // FIX: reset countdown UI before camera restarts on retake.
    if (cameraPermission !== 'denied') {
      await startCamera();
    }
  };

  const submitPhoto = async () => {
    const authToken = sessionStorage.getItem('authToken');
    if (!authToken) {
      setError('Authentication required. Please login again.');
      return;
    }

    const photoData = capturedPhoto;
    if (!photoData || !photoSource) {
      setError('Please capture or upload a photo before continuing.');
      return;
    }
    if (photoData.length < 5000) {
      setError('Photo is blank or invalid. Please retake.'); // FIX: client-side final guard before verify-photo request.
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      await apiClient.post(
        '/assessment/verify-photo',
        {
          assessment_key: assessmentKey,
          photo_data: photoData,
          source: photoSource
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      stopStream();
      onVerified();
    } catch (err) {
      const apiError = err?.response?.data?.detail || err?.response?.data?.error || err?.response?.data?.message; // FIX: surface detailed backend failure reason for debugging.
      setError(apiError || err?.message || 'Unable to submit photo verification. Please try again.'); // FIX: retain detailed actionable error message.
    } finally {
      setIsSubmitting(false);
    }
  };

  if (validationLoading) {
    return (
      <div className="min-h-screen bg-[rgba(10,10,20,0.92)] backdrop-blur-xl px-4 py-8 flex items-center justify-center">
        <div className="text-white/80 text-sm">Validating assessment link...</div>
      </div>
    );
  }

  if (!linkValid) {
    return (
      <div className="min-h-screen bg-[rgba(10,10,20,0.92)] backdrop-blur-xl px-4 py-8 flex items-center justify-center">
        <div className="w-full max-w-[520px] rounded-3xl p-7 aptitude-surface text-center">
          <h1 className="font-sora text-2xl text-white">Invalid or Expired Link</h1>
          <p className="mt-2 text-sm text-white/70">{linkError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[rgba(10,10,20,0.92)] backdrop-blur-xl px-4 py-8 flex items-center justify-center">
      <div className="w-full max-w-[560px] rounded-3xl p-7 aptitude-surface">
        <p className="text-xs tracking-wide uppercase text-white/60 text-center">Step 1 of 2: Identity Verification</p>
        <h1 className="font-sora text-2xl text-white text-center mt-2">{assessmentLabel}</h1>
        <p className="mt-2 text-sm text-white/70 text-center">
          Capture a clear photo before starting the assessment.
        </p>

        <div className="mt-6 flex justify-center">
          <div className="relative w-[300px] h-[300px] rounded-full overflow-hidden border-4 border-amber-400/70 aptitude-pulse-ring bg-black/30">
            {capturedPhoto && ( // FIX: render captured preview as overlay instead of replacing video DOM node.
              <img src={capturedPhoto} alt="Captured" className="absolute inset-0 h-full w-full object-cover z-10" /> // FIX: keep preview positioned over persistent video element.
            )}
            {cameraPermission === 'denied' && ( // FIX: render denied state message as overlay without unmounting video element.
              <div className="absolute inset-0 h-full w-full flex items-center justify-center text-center text-white/70 px-6 text-sm z-10"> // FIX: overlay denied message while preserving underlying video DOM.
                Camera access is required for this assessment. You may upload a clear photo instead.
              </div>
            )}
            <video // FIX: keep video always mounted so ref exists before and after cameraPermission state transitions.
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="absolute inset-0 h-full w-full object-cover" // FIX: ensure video always fills the circle container footprint.
              style={{
                display: capturedPhoto || cameraPermission === 'denied' ? 'none' : 'block', // FIX: keep video mounted; hide only for captured-photo and denied states.
              }}
            />
            {capturedPhoto ? (
              <span className="absolute right-4 bottom-4 bg-emerald-500 rounded-full p-1.5 text-white shadow-lg">
                <CheckCircle2 className="h-5 w-5" />
              </span>
            ) : null}
            {!capturedPhoto && countdown !== null ? (
              <div className="absolute inset-0 bg-black/45 flex items-center justify-center text-white text-4xl font-bold">
                {countdown > 0 ? countdown : 'Capturing'}
              </div>
            ) : null}
          </div>
        </div>

        <canvas ref={canvasRef} className="hidden" />

        {!capturedPhoto && cameraPermission !== 'denied' ? (
          <button
            type="button"
            onClick={captureFromVideo}
            className="mt-6 mx-auto h-[72px] w-[72px] rounded-full bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center shadow-xl shadow-amber-500/40"
          >
            <Camera className="h-7 w-7" />
          </button>
        ) : null}

        {(cameraPermission === 'denied' || !capturedPhoto) ? (
          <label className="mt-6 w-full rounded-xl border border-white/20 bg-white/10 text-white px-4 py-3 text-sm flex items-center justify-center gap-2 cursor-pointer">
            <Upload className="h-4 w-4" />
            {uploadedFile ? uploadedFile.name : 'Upload a photo instead'}
            <input type="file" accept="image/jpeg,image/png" onChange={onUpload} className="hidden" />
          </label>
        ) : null}

        {error ? <p className="mt-4 text-sm text-red-300 text-center">{error}</p> : null}

        <div className="mt-6 flex flex-wrap items-center gap-2 justify-center">
          {capturedPhoto ? (
            <button
              type="button"
              onClick={handleRetake}
              className="px-4 py-2 rounded-lg border border-white/25 bg-white/10 text-white"
            >
              Retake
            </button>
          ) : null}
          <button
            type="button"
            onClick={submitPhoto}
            disabled={!capturedPhoto || !photoSource || isSubmitting}
            className="px-5 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting...' : 'Continue'}
          </button>
          {import.meta.env.DEV ? ( // FIX: temporary dev-only bypass to unblock testing while backend issues are investigated.
            <button
              type="button"
              onClick={() => onVerified()} // FIX: allow explicit developer bypass of verify submit in local development only.
              className="px-3 py-1 rounded border border-dashed border-white/40 text-[11px] text-white/70"
            >
              [DEV] Skip verification
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
